/**
 * Product Interactions Manager - Moon Moon Agency
 * Gere les micro-interactions de la fiche produit :
 *   - Toggle "Voir plus/moins" sur la description tronquee
 *   - Color/size pill availability sync
 *
 * Pattern Manager (cf. .claude/rules/javascript.md).
 *
 * Note : la wishlist est desormais geree par mm_wishlist.js (app Wishlist Power).
 */

/*********************************************************************************************
 * 🛍️ PRODUCT INTERACTIONS MANAGER
 */
const ProductInteractionsManager = {
	config: {
		selectors: {
			descriptionToggle:  '[data-description-toggle]',
			descriptionContent: '[data-description-content]',
			colorSelect:        '[data-color-select]',
		},
		classes: {
			expanded: 'is-expanded',
		},
	},

	state: {
		ready: false,
	},

	init() {
		this._bindEvents();
		this.state.ready = true;
		window.log('info', '🛍️ [PRODUCT_INTERACTIONS]', 'Initialized');
	},

	_bindEvents() {
		document.addEventListener('click', (e) => {
			const toggle = e.target.closest(this.config.selectors.descriptionToggle);
			if (toggle) {
				e.preventDefault();
				this._toggleDescription(toggle);
				return;
			}
			const colorBtn = e.target.closest(this.config.selectors.colorSelect);
			if (colorBtn) {
				e.preventDefault();
				this._selectColorFromPanel(colorBtn);
			}
		});

		// Capture phase : au change d'un radio COULEUR, update la disponibilite des pills taille.
		// Si la taille active devient indispo pour la nouvelle couleur, on bascule sur la 1ere dispo.
		document.addEventListener('change', (e) => {
			const radio = e.target;
			if (!radio?.matches?.('[data-variant-input]:checked')) return;
			if (this._isInternalRadioSync) return;
			if (!this._isColorRadio(radio)) return;
			this._updateNonColorOptionsAvailability(radio.value);
		}, true);

		// Init : applique la disponibilite des pills selon la couleur initiale
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => { this._initSizesAvailability(); this._initDescriptions(); });
		} else {
			this._initSizesAvailability();
			this._initDescriptions();
		}

		// Re-mesure les descriptions apres chargement des fonts (la hauteur du texte peut changer)
		document.fonts?.ready?.then(() => this._initDescriptions());
		// Customizer : recharge de la section produit
		document.addEventListener('shopify:section:load', () => this._initDescriptions());

		// Re-applique le filter apres CHAQUE swap DOM (le variant_size block a data-product-reload,
		// son outerHTML est remplace ce qui ecrase nos classes/disabled).
		// Debounce 80ms : laisse plusieurs swap-events s'enchainer puis re-applique une seule fois.
		const debouncedReapply = () => {
			clearTimeout(this._reapplyTimer);
			this._reapplyTimer = setTimeout(() => this._initSizesAvailability(), 80);
		};
		document.addEventListener('product:variant-found', debouncedReapply);
		document.addEventListener('product:product-reload-element-updated', debouncedReapply);

		// Refresh is-selected sur le panel couleur quand il s'ouvre (le panel n'est pas re-rendu
		// par data-product-reload, il faut donc le synchroniser en JS sur l'event open).
		document.addEventListener('global:product-panel-opened', (e) => {
			if (e.detail?.name === 'color') this._refreshColorPanelSelection();
		});
	},

	_refreshColorPanelSelection() {
		const panel = document.querySelector('[data-panel="color"]');
		if (!panel) return;
		const checked = Array.from(document.querySelectorAll('[data-variant-input]:checked'));
		const colorRadio = checked.find(r => this._isColorRadio(r));
		const value = colorRadio?.value;
		if (!value) return;
		panel.querySelectorAll('.mm-side-panel-color-card').forEach(card => {
			const isMatch = card.dataset.colorValue === value;
			card.classList.toggle('is-selected', isMatch);
			if (isMatch) card.setAttribute('aria-current', 'true');
			else card.removeAttribute('aria-current');
		});
	},

	_initSizesAvailability() {
		const checkedColor = document.querySelectorAll('[data-variant-input]:checked');
		const colorRadio = Array.from(checkedColor).find(r => this._isColorRadio(r));
		if (colorRadio) this._updateNonColorOptionsAvailability(colorRadio.value);
	},

	_isColorRadio(radio) {
		const name = (radio?.name || '').toLowerCase();
		/* Matche toutes les racines color multi-langue. Aligne sur les listes utilisees
		   dans mm-product-variant-size.liquid et mm-product-variant-color.liquid.
		   Bug historique : "color".includes("cor") = true mais "cor".includes("color") = false.
		   Sans regex avec word-boundary, les locales pt/it (Cor, Colore) etaient invisibles
		   -> _updateNonColorOptionsAvailability jamais appele -> tailles inexistantes
		   restaient affichees + bug de selection couleur depuis le drawer. */
		return /\b(?:couleur|colour|color|farbe|kleur|colore|cor)\b|цвет|колір|颜色|顏色/.test(name);
	},

	/**
	 * Pour chaque option NON-couleur (taille, etc.) : distingue 3 etats par pill :
	 *  - EXISTS + AVAILABLE  -> pill clickable, classes nettoyees
	 *  - EXISTS + sold-out   -> classe --soldout, disabled
	 *  - !EXISTS             -> hidden + classe --unavailable, disabled
	 *    (cas "cette taille n'existe simplement pas pour cette couleur" -
	 *    different de sold-out qui suggere une rupture temporaire)
	 *
	 * Bascule : si la pill cochee n'est plus AVAILABLE pour la nouvelle couleur,
	 * on switch sur la 1ere dispo. Le firstAvailable est forcement EXISTS, donc
	 * on ne bascule jamais vers une pill cachee.
	 */
	_updateNonColorOptionsAvailability(colorValue) {
		const variants = this._getProductVariantsCached();
		if (!variants?.length) return;

		const allRadios = Array.from(document.querySelectorAll('[data-variant-input]'));
		const nonColorRadios = allRadios.filter(r => !this._isColorRadio(r));

		// Group par name d'option (ex: tous les radios taille)
		const groups = {};
		for (const r of nonColorRadios) {
			(groups[r.name] = groups[r.name] || []).push(r);
		}

		Object.values(groups).forEach((group) => {
			let firstAvailable = null;
			let checkedStillAvailable = false;

			group.forEach((radio) => {
				const value = radio.value;
				// Cherche toutes les variantes [couleur, value] pour distinguer exists vs available
				let exists = false;
				let isAvailable = false;
				for (const v of variants) {
					const opts = [v.option1, v.option2, v.option3];
					if (!opts.includes(colorValue) || !opts.includes(value)) continue;
					exists = true;
					if (v.available !== false) { isAvailable = true; break; }
				}

				radio.disabled = !isAvailable;
				const wrapper = radio.closest('.mm-size-pill, .mm-color-swatch, [data-variant-value]');
				if (wrapper) {
					wrapper.hidden = !exists;
					wrapper.classList.toggle('mm-size-pill--unavailable', !exists);
					wrapper.classList.toggle('mm-size-pill--soldout', exists && !isAvailable);
				}

				if (isAvailable) {
					if (!firstAvailable) firstAvailable = radio;
					if (radio.checked) checkedStillAvailable = true;
				}
			});

			// Bascule sur la 1ere dispo si la pill checkee est devenue indispo
			if (!checkedStillAvailable && firstAvailable && !firstAvailable.checked) {
				this._isInternalRadioSync = true;
				try {
					group.forEach(r => { r.checked = false; });
					firstAvailable.checked = true;
				} finally {
					this._isInternalRadioSync = false;
				}
				// Dispatch un change pour que variant-selector relise les radios
				setTimeout(() => {
					firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
				}, 0);
			}
		});
	},

	_getProductVariantsCached() {
		if (this._variantsCache) return this._variantsCache;
		const dataScript = document.querySelector('variant-selector script[data-variants-data]')
			|| document.querySelector('variant-selector script[type="application/json"]');
		try {
			this._variantsCache = JSON.parse(dataScript?.textContent || '[]');
		} catch (_) {
			this._variantsCache = [];
		}
		return this._variantsCache;
	},

	/**
	 * Selection couleur depuis le panel : trouve la 1ere variante disponible matchant
	 * cette valeur d'option, puis coche TOUS les radios correspondants (couleur + taille + ...).
	 * Cela evite le cas "couleur changee mais ancienne taille indispo pour cette couleur".
	 */
	_selectColorFromPanel(btn) {
		if (btn.disabled) return;
		const value = btn.dataset.colorValue;
		if (!value) return;

		// Recupere les variantes via le <script type="application/json"> du variant-selector
		const dataScript = document.querySelector('variant-selector script[data-variants-data]')
			|| document.querySelector('variant-selector script[type="application/json"]');
		let variants = [];
		try { variants = JSON.parse(dataScript?.textContent || '[]'); } catch (_) {}
		if (!Array.isArray(variants) || variants.length === 0) {
			window.log('warn', '🛍️ [PRODUCT_INTERACTIONS]', 'Variants data introuvable');
			return;
		}

		// Cherche la 1ere variante disponible matchant la valeur cliquee (peu importe l'option position)
		const matchesValue = (v) => [v.option1, v.option2, v.option3].includes(value);
		const variant = variants.find(v => matchesValue(v) && v.available !== false)
			|| variants.find(matchesValue);
		if (!variant) {
			window.log('warn', '🛍️ [PRODUCT_INTERACTIONS]', 'Aucune variante pour cette couleur', { value });
			return;
		}

		// Coche les radios pour chaque option de la variante trouvee.
		// Subtilites :
		//  - Si la nouvelle couleur n'a pas la taille actuellement checkee, le radio
		//    de la taille cible peut etre disabled (mm-size-pill--unavailable
		//    pose par _updateNonColorOptionsAvailability sur la couleur precedente).
		//  - Forcer .checked = true sur un radio disabled NE decoche PAS automatiquement
		//    les autres radios du meme group (radio mutex skip les disabled).
		//    -> On decoche manuellement + on enable le radio cible.
		//  - _updateNonColorOptionsAvailability re-evaluera l'etat post-change pour
		//    re-disable / hide ce qui doit l'etre selon la nouvelle couleur.
		this._isInternalRadioSync = true;
		const allRadios = Array.from(document.querySelectorAll('[data-variant-input]'));
		try {
			['option1', 'option2', 'option3'].forEach((key) => {
				const optVal = variant[key];
				if (!optVal) return;
				const r = allRadios.find(rd => rd.value === optVal);
				if (!r) return;
				// Decoche manuellement les autres du meme group avant de cocher la cible.
				allRadios.forEach(rd => {
					if (rd.name === r.name && rd !== r) rd.checked = false;
				});
				r.disabled = false;
				r.checked = true;
			});
		} finally {
			this._isInternalRadioSync = false;
		}

		// Re-evalue immediatement la disponibilite des pills non-couleur AVANT le
		// dispatch change. Ne depend plus du listener capture qui filtre via
		// _isColorRadio (et qui ratait silencieusement les locales non FR/EN avant
		// l'extension de la regex). Si la taille checkee n'est plus dispo pour la
		// nouvelle couleur, _updateNonColorOptionsAvailability bascule sur la 1ere
		// dispo via son propre dispatch differe.
		this._updateNonColorOptionsAvailability(value);

		// Dispatch le change sur le radio COULEUR (de preference) pour declencher
		// variant-selector. Fallback sur le premier checked si _isColorRadio rate
		// (defense en profondeur).
		const triggerRadio = allRadios.find(r => r.checked && this._isColorRadio(r))
			|| allRadios.find(r => r.checked);
		if (triggerRadio) {
			triggerRadio.dispatchEvent(new Event('change', { bubbles: true }));
		}
		window.MMProductPanels?.close?.();
	},

	/**
	 * Masque le bouton "Voir plus" quand la description tient entierement
	 * dans la hauteur repliee (rien a deplier). Sinon l'affiche.
	 */
	_initDescriptions() {
		document.querySelectorAll('[data-product-description]').forEach(wrapper => {
			const content = wrapper.querySelector(this.config.selectors.descriptionContent);
			const toggle = wrapper.querySelector(this.config.selectors.descriptionToggle);
			if (!content || !toggle) return;

			/* Mesure dans l'etat replie : scrollHeight (contenu total) vs clientHeight (hauteur visible) */
			const overflows = content.scrollHeight > content.clientHeight + 2;
			if (overflows) {
				toggle.hidden = false;
			} else {
				/* Rien a deplier : on masque le bouton et on retire le degrade/clamp */
				toggle.hidden = true;
				content.classList.add(this.config.classes.expanded);
			}
		});
	},

	_toggleDescription(toggle) {
		const wrapper = toggle.closest('[data-product-description]');
		const content = wrapper?.querySelector(this.config.selectors.descriptionContent);
		if (!content) return;
		const expanded = content.classList.toggle(this.config.classes.expanded);
		toggle.setAttribute('aria-expanded', String(expanded));
		toggle.textContent = expanded
			? (toggle.dataset.less || 'Voir moins')
			: (toggle.dataset.more || 'Voir plus');
	},

};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ProductInteractionsManager.init());
} else {
	ProductInteractionsManager.init();
}

window.MMProductInteractions = ProductInteractionsManager;
